<template>
  <div id="contract-editor-client-form" class="box">
    <b-container>
      <h2>{{ $t('CLIENT_INFO') }}</h2>

      <b-row>
        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('CITIZEN_ID')"
            v-model="citizenId"
            required
            borderbottom
            ref="citizenIdInput"
            :bounce="1000"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('PRIMARY_PHONE_NUMBER')"
            v-model="primaryPhoneNumber"
            type="phoneNumber"
            required
            borderbottom
            ref="primaryPhoneNumberInput"
            :bounce="1000"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('SECONDARY_PHONE_NUMBER')"
            v-model="secondaryPhoneNumber"
            type="phoneNumber"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('FIRST_NAME')"
            v-model="firstName"
            required
            borderbottom
            ref="firstNameInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('LAST_NAME')"
            v-model="lastName"
            required
            borderbottom
            ref="lastNameInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <select-input
            :label="$t('GENDER')"
            v-model="gender"
            :items="genders"
            borderbottom
            required
            ref="genderInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <date-input
            v-model="dateOfBirth"
            :label="$t('DATE_OF_BIRTH')"
            required
            borderbottom
            :validate="validateDateOfBirth"
            :yearRange="[moment().subtract(100, 'year').year(), moment().subtract(15, 'year').year()]"
            ref="dateOfBirthInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <select-input
            :label="$t('PROVINCE_MUNICIPALITY')"
            v-model="city"
            :items="cities"
            required
            borderbottom
            ref="cityInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <select-input
            :label="$t('DISTRICT_PROVINCIAL_CITY')"
            v-model="district"
            :items="districts"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('ADDRESS')"
            v-model="address"
            borderbottom
            textarea
            required
            ref="addressInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('ZALO_ID')"
            v-model="zaloId"
            borderbottom
            ref="zaloIdInput"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect } from '@vue/composition-api';
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import TextInput from '@/components/TextInput';
import SelectInput from '@/components/SelectInput';
import DateInput from '@/components/DateInput';

import { DISPLAY_DATE_FORMAT, DATE_FORMAT, GENDERS } from '@/consts';

import ClientFormService from './ClientFormService';

const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'ClientForm',
  components: {
    TextInput,
    SelectInput,
    DateInput
  },
  props: {
    value: {
      type: Object,
      default: () => {}
    }
  },
  setup(props, { root, emit }) {
    const { locations } = useAppState(['locations']);

    const id = ref(null);
    const finfluxId = ref(null);
    const firstName = ref('');
    const lastName = ref('');
    const citizenId = ref('');
    const zaloId = ref('');
    const primaryPhoneNumber = ref('');
    const secondaryPhoneNumber = ref('');
    const city = ref(1);
    const district = ref(null);
    const address = ref('');
    const gender = ref(null);
    const clientId = ref(null);
    const dateOfBirth = ref('');

    const firstNameInput = ref(null);
    const lastNameInput = ref(null);
    const citizenIdInput = ref(null);
    const primaryPhoneNumberInput = ref(null);
    const cityInput = ref(null);
    const addressInput = ref(null);
    const genderInput = ref(null);
    const dateOfBirthInput = ref(null);

    const wrappedClient = computed(() => ({
      id: id.value || undefined,
      finfluxId: finfluxId.value || undefined,
      citizenId: citizenId.value,
      zaloId: zaloId.value || undefined,
      primaryPhoneNumber: primaryPhoneNumber.value,
      secondaryPhoneNumber: secondaryPhoneNumber.value || undefined,
      firstName: firstName.value,
      lastName: lastName.value,
      gender: gender.value,
      dateOfBirth: dateOfBirth.value ? moment(dateOfBirth.value, DISPLAY_DATE_FORMAT).format(DATE_FORMAT) : null,
      locationId: district.value || city.value,
      address: address.value
    }));

    const cities = computed(() => locations.value.filter((item) => (
      item.type.level === 1
    )).map((item) => ({
      text: item.name,
      value: item.id
    })));

    const districts = computed(() => locations.value.filter((item) => (
      (item.type.level === 2)
      && (item.parent === city.value)
    )).map((item) => ({
      text: item.name,
      value: item.id
    })));

    const genders = Object.keys(GENDERS).map((item) => ({
      text: root.$t(GENDERS[item]),
      value: GENDERS[item]
    }));

    const validateDateOfBirth = (val) => {
      const date = moment(val, DISPLAY_DATE_FORMAT, true);

      if (date.isValid()) {
        const currentDate = moment();
        if (currentDate.diff(date, 'years') >= 15) {
          return '';
        }
        return 'INVALID_AGE';
      }

      return 'INVALID_DATE_FORMAT';
    };

    const submit = () => {
      const inputs = [
        firstNameInput.value,
        lastNameInput.value,
        citizenIdInput.value,
        primaryPhoneNumberInput.value,
        cityInput.value,
        addressInput.value,
        genderInput.value,
        dateOfBirthInput.value
      ];

      let valid = true;

      inputs.forEach((input) => {
        const v = input.validateInput();
        valid = valid && v;
      });


      return valid;
    };

    watchEffect(() => {
      emit('input', wrappedClient.value);
    });

    const loadClientInfo = (val) => {
      if (val) {
        id.value = val.id || null;
        finfluxId.value = val.finfluxId || null;
        firstName.value = val.firstName || '';
        lastName.value = val.lastName || '';
        primaryPhoneNumber.value = val.primaryPhoneNumber || '';
        citizenId.value = val.citizenId || '';
        zaloId.value = val.zaloId || null;
        secondaryPhoneNumber.value = val.secondaryPhoneNumber || '';
        gender.value = val.gender || null;
        dateOfBirth.value = val.dateOfBirth ? moment(val.dateOfBirth).format(DISPLAY_DATE_FORMAT) : '';

        const loc = locations.value.find((l) => (l.id === val.locationId));
        if (loc) {
          if (loc.type.level === 1) {
            city.value = loc.id;
            district.value = null;
          } else if (loc.type.level === 2) {
            district.value = loc.id;
            city.value = loc.parent;
          }
        }

        address.value = val.address || '';
        clientId.value = val.id || null;
      }
    };

    watch(primaryPhoneNumber, async (val) => {
      if (val) {
        try {
          const client = await ClientFormService.getClientByPhoneNumber(val);
          loadClientInfo(client);
        } catch (e) {
          loadClientInfo({
            ...wrappedClient.value,
            primaryPhoneNumber: val,
            id: undefined
          });
        }
      }
    });

    watch(citizenId, async (val) => {
      if (val) {
        try {
          const client = await ClientFormService.getClientByCitizenId(val);
          loadClientInfo(client);
        } catch (e) {
          loadClientInfo({
            ...wrappedClient.value,
            citizenId: val,
            id: undefined
          });
        }
      }
    });

    return {
      firstName,
      lastName,
      citizenId,
      zaloId,
      primaryPhoneNumber,
      secondaryPhoneNumber,
      dateOfBirth,
      validateDateOfBirth,
      moment,
      city,
      district,
      cities,
      districts,
      address,
      gender,
      genders,
      submit,
      firstNameInput,
      lastNameInput,
      citizenIdInput,
      primaryPhoneNumberInput,
      cityInput,
      addressInput,
      genderInput,
      dateOfBirthInput,
      loadClientInfo
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#contract-editor-client-form {
  .date-of-birth-input {
    svg {
      cursor: pointer;
      transition: all .2s ease-in-out;

      &:hover {
        color: $paradisePink;
      }
    }

    .toggle-box-wrapper {
      top: 44px;
    }
  }
}
</style>
